<template>
    <VJoyDropdown v-if="isPlatformAdmin" direction="down" justify="right" size="small" data-testid="project-view-admin-actions-dropdown">
        <template #dropdown-button>
            <VJoyIconButton v-if="screenSizeStore.isMobileScreen" icon="chevron-down" variant="admin" size="xsmall" />
            <VJoyButton v-else variant="admin" size="xxsmall" icon="chevron-down" icon-position="right">Admin</VJoyButton>
        </template>
        <template #dropdown-items>
            <NuxtLink class="project-action" :to="`/project-admin/project/${projectInformation.contract.correlationId}`" external>
                Admin project
            </NuxtLink>
            <NuxtLink class="project-action" :to="`/project-freelancer/project/${params.projectId}`" external>Freelancer project view</NuxtLink>
            <div v-if="environnement !== 'PRODUCTION'" class="project-action" @click="handleShowDialog('time-travel')">Time travel</div>
        </template>
    </VJoyDropdown>
    <VJoyDropdown direction="down" justify="right" data-testid="project-view-actions-dropdown">
        <template #dropdown-button>
            <VJoyIconButton v-if="screenSizeStore.isMobileScreen" variant="ghost" size="small" icon="dots-vertical" />
            <VJoyButton v-else variant="ghost" size="small" icon="chevron-down" icon-position="right">
                {{ $t('projectclient.projectview.main.actions.more-actions.cta') }}
            </VJoyButton>
        </template>
        <template v-if="allActions.length > 0" #dropdown-items>
            <template v-for="action of allActions" :key="action.icon">
                <NuxtLink
                    v-if="action.link"
                    :to="`${host}${action.link}`"
                    external
                    class="project-action"
                    :data-testid="`project-action-${action.id}`"
                >
                    <VJoyIcon class="project-action_icon" :name="action.icon" />
                    <span class="project-action_label ellipsis">{{ action.label }}</span>
                </NuxtLink>
                <div v-else class="project-action" :data-testid="`project-action-${action.id}`" @click="action.action">
                    <VJoyIcon class="project-action_icon" :name="action.icon" />
                    <span class="project-action_label ellipsis">{{ action.label }}</span>
                </div>
            </template>
        </template>
    </VJoyDropdown>
    <EndProjectOverlay v-if="isEndingDialogVisible" @close:dialog="handleShowDialog" />
    <TimeTravelDialog v-if="isTimeTravelDialogVisible && environnement !== 'PRODUCTION'" @close:dialog="handleShowDialog" />
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useGuestHost, useMaltEnvironnement, useTranslation} from '#imports';
    import {VJoyButton, VJoyDropdown, VJoyIcon, VJoyIconButton} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import {ProjectStatus} from '~/openapi';
    import {EndProjectOverlay, TimeTravelDialog} from './project-actions';
    import {useProjectInformationStore, useScreenSizeStore, useUserStore} from '@/common/stores';
    import {useRenewProject} from '@/features/project-view/composables';

    const {params} = useRoute('project-projectId-index');
    const userStore = useUserStore();
    const {isPlatformAdmin} = storeToRefs(userStore);
    const environnement = useMaltEnvironnement();

    const screenSizeStore = useScreenSizeStore();

    const {t} = useTranslation();
    const host = useGuestHost();
    const {roles} = storeToRefs(userStore);

    interface DropDownAction {
        link?: string;
        action?: () => void;
        label: string;
        icon: JoyIconsId;
        condition: boolean;
        id: string;
    }

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const {openRenewalDrawer} = useRenewProject();

    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToRenewProject = computed(() => roles.value.includes('ROLE_ADMIN'));
    const hasAuthorityToEndProject = computed(() => roles.value.includes('ROLE_MISSION_VALIDATION'));
    const hasAuthorityToTechSupport = computed(() => roles.value.includes('ROLE_TECHNICAL_SUPPORT'));

    const isEndingDialogVisible = ref(false);
    const isTimeTravelDialogVisible = ref(false);

    function handleShowDialog(modal: 'renew' | 'end' | 'time-travel') {
        if (modal === 'renew') {
            openRenewalDrawer();
        } else if (modal === 'end') {
            isEndingDialogVisible.value = !isEndingDialogVisible.value;
        } else if (modal === 'time-travel') {
            isTimeTravelDialogVisible.value = !isTimeTravelDialogVisible.value;
        }
    }

    const allActions = computed<DropDownAction[]>(() => {
        const actionList: DropDownAction[] = [
            {
                id: 'renew-project',
                icon: 'refresh',
                label: t('projectclient.projectview.main.actions.renew.project'),
                action() {
                    handleShowDialog('renew');
                },
                condition: projectInformation.value.renewStatus === 'RENEWABLE' && (isParticipant.value || hasAuthorityToRenewProject.value),
            },
            {
                id: 'end-project',
                icon: 'cross',
                label: t('projectclient.projectview.main.actions.end.project'),
                action() {
                    handleShowDialog('end');
                },
                condition:
                    projectInformation.value.contract.type === 'COST_PLUS' &&
                    !projectInformation.value.completionRequested &&
                    projectInformation.value.projectStatus === ProjectStatus.InProgress &&
                    (isParticipant.value || hasAuthorityToEndProject.value),
            },
            {
                id: 'mark-project-complete',
                icon: 'check',
                label: t('projectclient.projectview.main.actions.complete.project.complete-project'),
                link: `/project-client/project/${params.projectId}/validation?origin=project-view`,
                condition:
                    projectInformation.value.contract.type === 'FIXED_PRICE' &&
                    !projectInformation.value.completionRequested &&
                    projectInformation.value.projectStatus === ProjectStatus.InProgress &&
                    projectInformation.value.options?.canCloseProject === 'CAN_CLOSE' &&
                    (isParticipant.value || hasAuthorityToEndProject.value),
            },
            {
                id: 'expense-list',
                icon: 'single-report',
                label: t('projectclient.projectview.main.actions.view.expenses'),
                link: `/project-client/project/${params.projectId}/expense-list`,
                condition: isPlatformAdmin.value && !!projectInformation.value.expenseTrackerEnable,
            },
        ];

        return actionList.filter((action) => !!action.condition);
    });
</script>

<style lang="scss" scoped>
    :deep(.joy-dropdown-list__items) {
        padding: 0 !important;
    }

    .project-action {
        height: 48px;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        column-gap: var(--joy-core-spacing-3);
        padding: 0 var(--joy-core-spacing-3);
        transition: background-color 0.3s ease;
        cursor: pointer;

        &:hover {
            background-color: var(--joy-color-secondary-10);
        }
    }
</style>
